import { useCallback, useEffect, useRef, useState } from 'react';
import useMedia from '../hooks/useMedia';

const FILTERS = [
  { label: 'Alle', value: '' },
  { label: 'Bilder', value: 'image' },
  { label: 'Videos', value: 'video' },
  { label: 'Dokumente', value: 'application' },
];

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ThumbPlaceholder({ item }) {
  const icon = item.mediaType === 'video' ? '🎬'
    : item.mediaType === 'audio' ? '🎵'
    : item.mediaType === 'application' ? '📄'
    : '📁';
  return (
    <div className="media-thumb-placeholder">
      <span>{icon}</span>
      <span className="muted small" style={{ marginTop: '0.3rem' }}>
        {item.contentType?.split('/')[1]?.toUpperCase() || item.mediaType}
      </span>
    </div>
  );
}

function MediaCard({ item, onPreview, onDelete }) {
  const isProcessing = item.status === 'pending-upload' && item.mediaType === 'image';
  const isImage = item.mediaType === 'image' || item.contentType?.startsWith('image/');

  return (
    <article className={`media-card${item.status === 'pending-upload' ? ' processing' : ''}`}>
      <div className="media-card-thumb" onClick={() => onPreview(item)}>
        {isProcessing ? (
          <div className="media-card-processing">
            <span className="muted small">Wird verarbeitet…</span>
          </div>
        ) : item.thumbnailUrl ? (
          <img src={item.thumbnailUrl} alt={item.altText || item.filename} loading="lazy" />
        ) : isImage ? (
          <div className="media-card-processing">
            <span className="muted small">Kein Vorschaubild</span>
          </div>
        ) : (
          <ThumbPlaceholder item={item} />
        )}
      </div>
      <div className="media-card-body">
        <div className="media-card-title" title={item.filename}>{item.filename}</div>
        <div className="media-card-meta">
          {item.width && item.height ? <span>{item.width}×{item.height}</span> : null}
          {item.size ? <span>{formatBytes(item.size)}</span> : null}
        </div>
        <div className="media-card-actions">
          <button className="button ghost small" onClick={() => onPreview(item)}>Vorschau</button>
          <button className="button danger small" onClick={() => onDelete(item)}>✕</button>
        </div>
      </div>
    </article>
  );
}

export default function MediaLibrary() {
  const { listMedia, createUpload, deleteMedia, getMediaItem } = useMedia();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const pollRef = useRef(null);

  const loadMedia = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await listMedia({ limit: 100, ...(filter ? { type: filter } : {}) });
      setItems(response.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [listMedia, filter]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  // Poll until all pending items become ready (after upload)
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const response = await listMedia({ limit: 100, ...(filter ? { type: filter } : {}) });
        const next = response.items || [];
        setItems(next);
        const stillPending = next.some((i) => i.status === 'pending-upload');
        if (!stillPending || attempts >= 20) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 3000);
  }, [listMedia, filter]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    setError('');
    try {
      for (const file of files) {
        const { uploadUrl } = await createUpload({
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          mediaType: file.type?.split('/')[0] || 'file',
          size: file.size,
        });
        const res = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!res.ok) throw new Error(`Upload fehlgeschlagen: ${file.name}`);
      }
      await loadMedia();
      // Start polling so thumbnails appear as soon as Lambda finishes
      startPolling();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const openPreview = async (item) => {
    setPreviewLoading(true);
    setPreview({ ...item, _loading: true });
    try {
      const response = await getMediaItem(item.mediaId);
      setPreview(response.item);
    } catch (err) {
      setPreview({ ...item, _error: err.message });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDelete = async (item) => {
    try {
      await deleteMedia(item.mediaId);
      setConfirmDelete(null);
      setItems((prev) => prev.filter((i) => i.mediaId !== item.mediaId));
    } catch (err) {
      setError(err.message);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    uploadFiles(Array.from(e.dataTransfer.files || []));
  };

  const filteredItems = filter
    ? items.filter((i) => i.mediaType === filter || i.contentType?.startsWith(filter + '/'))
    : items;

  return (
    <div className="stack gap-lg">
      {/* Header */}
      <section className="card">
        <div className="row between responsive-gap">
          <div>
            <h2>Medien-Bibliothek</h2>
            <p className="muted">Bilder, Dokumente und andere Dateien verwalten.</p>
          </div>
          <label className={`button primary file-button${uploading ? ' disabled' : ''}`}>
            {uploading ? 'Wird hochgeladen…' : '+ Hochladen'}
            <input
              type="file"
              multiple
              accept="image/*,video/*,application/pdf"
              onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
              hidden
              disabled={uploading}
            />
          </label>
        </div>
        {error ? <div className="alert error" style={{ marginTop: '0.75rem' }}>{error}</div> : null}
      </section>

      {/* Filter bar */}
      <div className="media-filter-bar">
        {FILTERS.map(({ label, value }) => (
          <button
            key={value}
            type="button"
            className={`button${filter === value ? ' primary' : ' ghost'} small`}
            onClick={() => setFilter(value)}
          >
            {label}
          </button>
        ))}
        <span className="muted small" style={{ marginLeft: 'auto' }}>
          {filteredItems.length} {filteredItems.length === 1 ? 'Datei' : 'Dateien'}
        </span>
      </div>

      {/* Drop zone + grid */}
      <div
        className={`media-upload-zone${dragging ? ' drag-over' : ''}`}
        onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }}
        onDrop={onDrop}
      >
        {dragging && (
          <div className="media-drop-hint">
            <span>Dateien hier ablegen</span>
          </div>
        )}

        {loading ? (
          <div className="media-grid">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="media-card processing">
                <div className="media-card-thumb"><div className="media-card-processing" /></div>
                <div className="media-card-body"><div className="shimmer-line" /><div className="shimmer-line short" /></div>
              </div>
            ))}
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="media-empty">
            <p className="muted">Keine Dateien gefunden. Ziehe Dateien hierher oder klicke "Hochladen".</p>
          </div>
        ) : (
          <div className="media-grid">
            {filteredItems.map((item) => (
              <MediaCard
                key={item.mediaId}
                item={item}
                onPreview={openPreview}
                onDelete={(i) => setConfirmDelete(i)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      {confirmDelete ? (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-header">
              <h3>Datei löschen?</h3>
              <button type="button" className="button ghost" onClick={() => setConfirmDelete(null)}>✕</button>
            </div>
            <p>„<strong>{confirmDelete.filename}</strong>" wird dauerhaft gelöscht.</p>
            <div className="button-row" style={{ marginTop: '1rem', justifyContent: 'flex-end' }}>
              <button className="button ghost" onClick={() => setConfirmDelete(null)}>Abbrechen</button>
              <button className="button danger" onClick={() => handleDelete(confirmDelete)}>Löschen</button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Preview modal */}
      {preview ? (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setPreview(null); }}>
          <div className="modal large">
            <div className="modal-header">
              <h3 style={{ wordBreak: 'break-all' }}>{preview.filename}</h3>
              <button type="button" className="button ghost" onClick={() => setPreview(null)}>✕</button>
            </div>
            {previewLoading ? (
              <div className="media-card-processing" style={{ height: 200 }} />
            ) : preview.contentType?.startsWith('image/') ? (
              <img
                className="preview-image"
                src={preview.mediumUrl || preview.accessUrl}
                alt={preview.altText || preview.filename}
              />
            ) : null}
            <div className="media-meta-grid">
              {preview.width && <div><strong>Dimensionen</strong><span>{preview.width} × {preview.height} px</span></div>}
              {preview.format && <div><strong>Format</strong><span>{preview.format?.toUpperCase()}</span></div>}
              {preview.size && <div><strong>Größe</strong><span>{formatBytes(preview.size)}</span></div>}
              {preview.uploadedAt && <div><strong>Hochgeladen</strong><span>{new Date(preview.uploadedAt).toLocaleString('de-DE')}</span></div>}
              {preview.uploadedBy && <div><strong>Von</strong><span>{preview.uploadedBy}</span></div>}
            </div>
            {preview.accessUrl && (
              <div style={{ marginTop: '1rem' }}>
                <label>
                  URL
                  <div className="row" style={{ gap: '0.5rem', marginTop: '0.25rem' }}>
                    <input readOnly value={preview.accessUrl} style={{ flex: 1 }} />
                    <button
                      className="button ghost small"
                      onClick={() => navigator.clipboard.writeText(preview.accessUrl)}
                    >
                      Kopieren
                    </button>
                  </div>
                </label>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
